import React, { createContext, useContext, useState, useEffect } from 'react';
import type { Locale, TranslationSchema } from './types';
import { en } from './locales/en';
import { tr } from './locales/tr';
import { es } from './locales/es';

export * from './types';

const translations: Record<Locale, TranslationSchema> = {
  en,
  tr,
  es,
};

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationSchema;
  availableLocales: Array<{ code: Locale; label: string; flag: string }>;
}

export const availableLocales: Array<{ code: Locale; label: string; flag: string }> = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const I18nContext = createContext<I18nContextType | null>(null);

const STORAGE_KEY = 'workouse_locale';

/**
 * Resolves initial locale by prioritizing:
 * 1. URL Query parameter (?lang=tr / ?lang=es / ?lang=en)
 * 2. Saved user preference in localStorage
 * 3. Browser navigator language
 * 4. Default fallback: 'en'
 */
export function getInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';

  // 1. URL query string priority
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang')?.toLowerCase();
    if (langParam === 'en' || langParam === 'tr' || langParam === 'es') {
      localStorage.setItem(STORAGE_KEY, langParam);
      return langParam;
    }
  } catch {
    // Fallback if URL parsing fails
  }

  // 2. Local storage preference
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && (saved === 'en' || saved === 'tr' || saved === 'es')) {
      return saved;
    }
  } catch {
    // Fallback if localStorage is disabled
  }

  // 3. Browser language detection
  const browserLang = navigator.language?.toLowerCase() || '';
  if (browserLang.startsWith('tr')) return 'tr';
  if (browserLang.startsWith('es')) return 'es';

  return 'en';
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, newLocale);
        document.documentElement.lang = newLocale;

        // Synchronize query parameter in URL without triggering full page reload
        const url = new URL(window.location.href);
        if (newLocale === 'en') {
          url.searchParams.delete('lang');
        } else {
          url.searchParams.set('lang', newLocale);
        }
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch {
        // Fallback for isolated contexts
      }
    }
  };

  // Sync DOM lang and listen for back/forward browser navigation
  useEffect(() => {
    document.documentElement.lang = locale;

    const handlePopState = () => {
      const currentLocale = getInitialLocale();
      setLocaleState(currentLocale);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [locale]);

  const value: I18nContextType = {
    locale,
    setLocale,
    t: translations[locale] || translations.en,
    availableLocales,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
